<template>
  <div class="flex w-full" style="position: relative">
    <div v-if="gliders.length" class="d-flex flex-col w-full h-full">
      <div class="flex-grow-0 w-full">
        <v-tabs v-model="selectedGliderIdx" align-with-title class="flex-grow-0">
          <v-tab>
            <v-icon class="mr-2 !text-black">{{ fleetIco }}</v-icon>
            {{ fleet.FLE_LIBELLE }}
          </v-tab>
          <v-tab v-for="(glider, idx) in gliders" :key="glider.id" :title="glider.libelle">
            {{ glider.libelle }}
          </v-tab>
        </v-tabs>
      </div>
      <div class="flex flex-col relative" v-if="selectedGliderIdx == 0">
        <v-card class="ml-2 mr-3 my-2">
          <v-card-title>
            <div class="flex flex-col">
              <span class="subheading">{{ $t('Pilot.FileTransfert.LastFileTitle') }}</span>
              <div class="flex gap-2">
                <v-text-field
                  v-model="configLastFile.nbDownLastNavFile"
                  :label="$t('Pilot.FileTransfert.NavFieldName')"
                />
                <v-switch
                  class="px-0 pb-2 sm-switch"
                  hide-details
                  v-model="configLastFile.enableCatchUpNav"
                  :label="$t('Pilot.FileTransfert.EnableCatchUp')"
                />
              </div>
              <div class="flex gap-2">
                <v-text-field
                  v-model="configLastFile.nbDownLastPldFile"
                  :label="$t('Pilot.FileTransfert.PldFieldName')"
                />
                <v-switch
                  class="px-0 pb-2 sm-switch"
                  hide-details
                  v-model="configLastFile.enableCatchUpPld"
                  :label="$t('Pilot.FileTransfert.EnableCatchUp')"
                />
              </div>
              <div class="flex gap-2">
                <v-btn @click.prevent="restoreConfigLastFile()" small class="ml-auto">
                  {{ $t('Fleet.Clear') }}
                  <v-icon small right>mdi-replay</v-icon>
                </v-btn>
                <v-btn @click.prevent="applyToAll()" :loading="saving" :disabled="saving" small color="primary">
                  {{ $t('Fleet.ApplyToAll') }}
                  <v-icon small right>mdi-content-save</v-icon>
                </v-btn>
              </div>
            </div>
          </v-card-title>
        </v-card>
        <v-card class="ml-2 mr-3 my-2">
          <v-card-title style="flex-direction: column; align-items: stretch; height: 100%">
            <span class="subheading" style="flex: 0 0 auto">{{ $t('Pilot.FileTransfert.DownloadStatus') }}</span>
            <v-layout row wrap mt-2 style="flex: 0 0 auto">
              <v-flex class="centercontent" xs4 pr-2>
                <span class="body-2">{{ $t('Pilot.FileTransfert.Cycle') }}</span>
              </v-flex>
              <v-flex class="centercontent" xs4 pl-2 pr-2>
                <span class="body-2">{{ $t('Pilot.FileTransfert.NavFieldName') }}</span>
                <!-- <v-btn small icon @click="downloadFile(`/api/gliderfile/download/gli/${mission.misId}/all`)">
                  <v-icon>mdi-download</v-icon>
                </v-btn> -->
              </v-flex>
              <v-flex class="centercontent" xs4 pl-2>
                <span class="body-2">{{ $t('Pilot.FileTransfert.PldFieldName') }}</span>
                <!-- <v-btn small icon @click="downloadFile(`/api/gliderfile/download/pld1/${mission.misId}/all`)">
                  <v-icon>mdi-download</v-icon>
                </v-btn> -->
              </v-flex>
            </v-layout>
            <v-layout row wrap style="flex: 1 0; overflow: auto; align-content: flex-start">
              <template v-for="(glider, idx) in gliders">
                <v-flex class="centercontent" :key="glider.id" xs4 pr-2>{{ glider.libelle }}</v-flex>
                <v-flex class="centercontent" :key="`nav_${glider.id}`" xs4 pl-2 pr-2>
                  <v-btn small icon @click="downloadFile(`/api/gliderfile/download/gli/${glider.id}/all`)">
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                </v-flex>
                <v-flex class="centercontent" :key="`pld_${glider.id}`" xs4 pl-2>
                  <v-btn small icon @click="downloadFile(`/api/gliderfile/download/pld1/${glider.id}/all`)">
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                </v-flex>
              </template>
            </v-layout>
          </v-card-title>
        </v-card>
      </div>
      <mission-detail-file-transfer
        v-else
        :uiid="uiid"
        :isleft="isleft"
        :mission="store.myGetters.missionsById[gliders[selectedGliderIdx - 1].id]"
        :isVisible="props.isVisible"
        :key="selectedGliderIdx"
      />
    </div>
    <div v-else class="w-full h-full flex flex-col mx-4 items-center justify-center title gap-2">
      <span>{{ $t('Fleet.NoGliders') }}</span>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import store from '@/store'
import apiHelpers from '@/utils/api'
import { AloDbFleet, isAloDbFleetCoverage, isAloDbFleetFormation } from '@/models/alodb/AloDbFleet'
import MissionDetailFileTransfer from '@/components/mission/MissionDetailFileTransfer.vue'

const props = defineProps<{
  fleet: AloDbFleet
  isleft: boolean
  uiid: string
  isVisible: boolean
}>()

const fleetIco = computed(() => {
  return isAloDbFleetCoverage(props.fleet)
    ? 'mdi-select-group'
    : isAloDbFleetFormation(props.fleet)
    ? 'mdi-vector-circle'
    : 'mdi-multicast'
})

const missions = computed(
  () => props.fleet.activeMissionIds?.map(id => store.myGetters?.missionsById[id])?.filter(m => !!m) ?? [],
)

const gliders = computed(() => missions.value.map(m => ({ id: m.misId, libelle: m.gliKey })))

const configLastFile = computed(() => store.state.fleet.context?.configLastFile)

onMounted(() => {
  if (
    !store.state.fleet.context?.configLastFile ||
    store.state.fleet.context.configLastFile.idFleet != props.fleet.id
  ) {
    restoreConfigLastFile()
  }
})

const saving = ref(false)

function restoreConfigLastFile() {
  store.state.fleet.context.configLastFile = {
    enableCatchUpNav: true,
    enableCatchUpPld: true,
    nbDownLastNavFile: 1,
    nbDownLastPldFile: 1,
  }
}

async function applyToAll() {
  if (saving.value) return
  saving.value = true

  for (const mission of missions.value.filter(m => m.misActifOn)) {
    if (mission.gliId == null) return
    try {
      await store.myActions.GLIDER_IRIS_FILE_TRANSFERT_REQUEST(mission.gliId!)
      await store.myActions.GLIDER_UPDATE_MODIFIED_FILE_TRANSFERT({
        itemId: mission.gliId!,
        newProperties: {
          mod_settings: {
            FileTransfer_standard: {
              caught_old_file_nav: configLastFile.value.enableCatchUpNav,
              caught_old_file_sci: configLastFile.value.enableCatchUpPld,
              nb_file_nav: configLastFile.value.nbDownLastNavFile,
              nb_file_sci: configLastFile.value.nbDownLastPldFile,
            },
          },
        },
      })
      await store.myActions.GLIDER_IRIS_FILE_TRANSFERT_SAVE_REQUEST(mission.gliId!)
    } catch (e) {
      console.error(e)
    }
  }
  nextTick(() => {
    restoreConfigLastFile()
    saving.value = false
  })
}

function downloadFile(url: string) {
  apiHelpers.downloadFile(url, '.csv')
}

const selectedGliderIdx = ref(0)
</script>
